type ReleaseText = { [lang: string]: string };

type ReleaseNote = {
    icon: string;
    title: ReleaseText;
    body: ReleaseText;
};

type LogoChange = {
    from: string;
    to: string;
};

export type ReleaseEntry = {
    version: string;
    notable: boolean;
    logoChange?: LogoChange;
    eyebrow?: ReleaseText;
    title: ReleaseText;
    subtitle: ReleaseText;
    notes: ReleaseNote[];
};

type ReleaseNotesFile = {
    [version: string]: Omit<ReleaseEntry, 'version'>;
};

export const RELEASE_NOTES_PATH = 'assets/release-notes.json';

export const releaseText = (text: ReleaseText, lang: string): string =>
    (text && (text[lang] || text.en)) || '';

const compareVersions = (a: string, b: string): number => {
    const partsA = a.split('.');
    const partsB = b.split('.');
    const length = Math.max(partsA.length, partsB.length);
    for (let i = 0; i < length; i++) {
        const partA = partsA[i] ?? '';
        const partB = partsB[i] ?? '';
        const numA = parseInt(partA, 10);
        const numB = parseInt(partB, 10);
        if (Number.isNaN(numA) || Number.isNaN(numB)) {
            if (partA !== partB) return partA < partB ? -1 : 1;
        } else if (numA !== numB) {
            return numA < numB ? -1 : 1;
        }
    }
    return 0;
};

export const newestVersion = (
    a: string | null,
    b: string | null,
): string | null => {
    if (!a) return b || null;
    if (!b) return a;
    return compareVersions(a, b) >= 0 ? a : b;
};

export const parseReleaseNotes = (data: ReleaseNotesFile): ReleaseEntry[] =>
    Object.entries(data || {}).map(([version, entry]) => ({
        ...entry,
        version,
    }));

export const collectUnseenReleases = (
    releases: ReleaseEntry[],
    seenVersion: string | null,
): ReleaseEntry[] =>
    releases
        .filter(
            (entry) =>
                seenVersion === null ||
                compareVersions(entry.version, seenVersion) > 0,
        )
        .sort((a, b) => compareVersions(b.version, a.version));

export const latestRelease = (releases: ReleaseEntry[]): ReleaseEntry | null =>
    releases.reduce<ReleaseEntry | null>(
        (latest, entry) =>
            !latest || compareVersions(entry.version, latest.version) > 0
                ? entry
                : latest,
        null,
    );
